import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { userManagementAPI } from '../services/api';
import {
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserCircleIcon,
  BuildingOffice2Icon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { ShieldExclamationIcon } from '@heroicons/react/24/solid';

// ============================================================================
// Status Badge
// ============================================================================
function StatusBadge({ status }) {
  const styles = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-yellow-100 text-yellow-800',
    deleted: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {status || 'unknown'}
    </span>
  );
}

// ============================================================================
// Organization Tree Browser
// ============================================================================

function TreeNode({ label, level = 0, icon, badge, expandable = false, defaultExpanded = false, onExpand, children, onClick }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded && onExpand) onExpand();
  };

  return (
    <div>
      <div
        className={`flex items-center gap-2 min-h-touch px-3 py-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-all group`}
        style={{ paddingLeft: `${12 + level * 16}px` }}
        onClick={expandable ? handleToggle : onClick}
      >
        {expandable ? (
          <button className="flex-shrink-0 w-5 h-5 flex items-center justify-center" onClick={(e) => { e.stopPropagation(); handleToggle(); }}>
            {expanded ? (
              <ChevronDownIcon className="w-4 h-4 text-storm-blue" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-sky-blue" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}
        {icon}
        <span className="flex-1 text-sm font-medium truncate">{label}</span>
        {badge}
      </div>
      {expanded && children && <div>{children}</div>}
    </div>
  );
}

function OrgTreeBrowser({ orgTree, loading, error, onRetry }) {
  const navigate = useNavigate();
  const [groupMembers, setGroupMembers] = useState({});
  const [loadingGroups, setLoadingGroups] = useState({});

  const loadGroupMembers = useCallback(async (groupId) => {
    if (groupMembers[groupId] || loadingGroups[groupId]) return;
    setLoadingGroups((prev) => ({ ...prev, [groupId]: true }));
    try {
      const res = await userManagementAPI.getGroupMembers(groupId);
      setGroupMembers((prev) => ({ ...prev, [groupId]: res.data.members || res.data || [] }));
    } catch (err) {
      console.error('[OrgTree] Failed to load group members:', err);
      setGroupMembers((prev) => ({ ...prev, [groupId]: [] }));
    } finally {
      setLoadingGroups((prev) => ({ ...prev, [groupId]: false }));
    }
  }, [groupMembers, loadingGroups]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="w-6 h-6 text-storm-blue animate-spin" />
        <span className="ml-2 text-storm-blue">Loading organization tree...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-3">{error}</p>
        <button onClick={onRetry} className="text-sm font-semibold text-storm-blue hover:text-deep-navy transition-colors">
          Try Again
        </button>
      </div>
    );
  }

  if (!orgTree || orgTree.length === 0) {
    return (
      <p className="text-center text-sky-blue py-8">No organizations found</p>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {orgTree.map((org) => (
        <TreeNode
          key={org.id}
          label={org.name}
          level={0}
          expandable
          icon={<BuildingOffice2Icon className="w-5 h-5 text-deep-navy flex-shrink-0" />}
          badge={
            <span className="text-xs font-medium text-sky-blue bg-gray-100 px-2 py-0.5 rounded-full">
              {org.tier || 'free'}
            </span>
          }
        >
          {/* Campuses */}
          {(org.campuses || []).map((campus) => (
            <TreeNode
              key={campus.id}
              label={campus.name}
              level={1}
              expandable
              icon={<span className="w-2 h-2 rounded-full bg-storm-blue flex-shrink-0" />}
            >
              {/* Groups directly under campus (no ministry) */}
              {(campus.groups || []).map((group) => (
                <TreeNode
                  key={group.id}
                  label={group.name}
                  level={3}
                  expandable
                  onExpand={() => loadGroupMembers(group.id)}
                  icon={<span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />}
                  badge={
                    group.member_count != null && (
                      <span className="text-xs text-sky-blue bg-gray-100 px-2 py-0.5 rounded-full">
                        {group.member_count}
                      </span>
                    )
                  }
                >
                  {loadingGroups[group.id] ? (
                    <div className="flex items-center gap-2 py-2" style={{ paddingLeft: '76px' }}>
                      <ArrowPathIcon className="w-4 h-4 text-sky-blue animate-spin" />
                      <span className="text-xs text-sky-blue">Loading members...</span>
                    </div>
                  ) : (
                    (groupMembers[group.id] || []).map((member) => (
                      <TreeNode
                        key={member.id || member.user_id}
                        label={`${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email}
                        badge={member.role && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${member.role === 'group_leader' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{member.role === 'group_leader' ? 'Leader' : member.role === 'group_member' ? 'Member' : member.role.replace('_', ' ')}</span>}
                        level={4}
                        icon={<UserCircleIcon className="w-4 h-4 text-sky-blue flex-shrink-0" />}
                        onClick={() => navigate(`/user-management/users/${member.id || member.user_id}`)}
                      />
                    ))
                  )}
                  {!loadingGroups[group.id] && (groupMembers[group.id] || []).length === 0 && groupMembers[group.id] && (
                    <div className="text-xs text-sky-blue py-2" style={{ paddingLeft: '76px' }}>
                      No members
                    </div>
                  )}
                </TreeNode>
              ))}

              {/* Ministries */}
              {(campus.ministries || []).map((ministry) => (
                <TreeNode
                  key={ministry.id}
                  label={ministry.name}
                  level={2}
                  expandable
                  icon={<span className="w-2 h-2 rounded-full bg-sky-blue flex-shrink-0" />}
                >
                  {(ministry.groups || []).map((group) => (
                    <TreeNode
                      key={group.id}
                      label={group.name}
                      level={3}
                      expandable
                      onExpand={() => loadGroupMembers(group.id)}
                      icon={<span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />}
                      badge={
                        group.member_count != null && (
                          <span className="text-xs text-sky-blue bg-gray-100 px-2 py-0.5 rounded-full">
                            {group.member_count}
                          </span>
                        )
                      }
                    >
                      {loadingGroups[group.id] ? (
                        <div className="flex items-center gap-2 py-2" style={{ paddingLeft: '76px' }}>
                          <ArrowPathIcon className="w-4 h-4 text-sky-blue animate-spin" />
                          <span className="text-xs text-sky-blue">Loading members...</span>
                        </div>
                      ) : (
                        (groupMembers[group.id] || []).map((member) => (
                          <TreeNode
                            key={member.id || member.user_id}
                            label={`${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email}
                        badge={member.role && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${member.role === 'group_leader' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{member.role === 'group_leader' ? 'Leader' : member.role === 'group_member' ? 'Member' : member.role.replace('_', ' ')}</span>}
                            level={4}
                            icon={<UserCircleIcon className="w-4 h-4 text-sky-blue flex-shrink-0" />}
                            onClick={() => navigate(`/user-management/users/${member.id || member.user_id}`)}
                          />
                        ))
                      )}
                      {!loadingGroups[group.id] && (groupMembers[group.id] || []).length === 0 && groupMembers[group.id] && (
                        <div className="text-xs text-sky-blue py-2" style={{ paddingLeft: '76px' }}>
                          No members
                        </div>
                      )}
                    </TreeNode>
                  ))}
                </TreeNode>
              ))}
            </TreeNode>
          ))}

          {/* Groups directly under org (no campus) */}
          {(org.groups || []).map((group) => (
            <TreeNode
              key={group.id}
              label={group.name}
              level={1}
              expandable
              onExpand={() => loadGroupMembers(group.id)}
              icon={<span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />}
              badge={
                group.member_count != null && (
                  <span className="text-xs text-sky-blue bg-gray-100 px-2 py-0.5 rounded-full">
                    {group.member_count}
                  </span>
                )
              }
            >
              {loadingGroups[group.id] ? (
                <div className="flex items-center gap-2 py-2" style={{ paddingLeft: '44px' }}>
                  <ArrowPathIcon className="w-4 h-4 text-sky-blue animate-spin" />
                  <span className="text-xs text-sky-blue">Loading members...</span>
                </div>
              ) : (
                (groupMembers[group.id] || []).map((member) => (
                  <TreeNode
                    key={member.id || member.user_id}
                    label={`${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email}
                    badge={member.role && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${member.role === 'group_leader' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{member.role === 'group_leader' ? 'Leader' : member.role === 'group_member' ? 'Member' : member.role.replace('_', ' ')}</span>}
                    level={2}
                    icon={<UserCircleIcon className="w-4 h-4 text-sky-blue flex-shrink-0" />}
                    onClick={() => navigate(`/user-management/users/${member.id || member.user_id}`)}
                  />
                ))
              )}
              {!loadingGroups[group.id] && (groupMembers[group.id] || []).length === 0 && groupMembers[group.id] && (
                <div className="text-xs text-sky-blue py-2" style={{ paddingLeft: '44px' }}>
                  No members
                </div>
              )}
            </TreeNode>
          ))}
        </TreeNode>
      ))}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function UserManagementPage() {
  const navigate = useNavigate();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const debounceRef = useRef(null);

  // Abuse alerts state
  const [abuseAlerts, setAbuseAlerts] = useState([]);
  const [abuseLoading, setAbuseLoading] = useState(true);

  const loadAbuseAlerts = useCallback(async () => {
    setAbuseLoading(true);
    try {
      const res = await userManagementAPI.getAbuseAlerts({ status: 'new' });
      setAbuseAlerts(res.data.alerts || []);
    } catch (err) {
      console.error('[UserManagement] Abuse alerts error:', err);
    } finally {
      setAbuseLoading(false);
    }
  }, []);

  // Org tree state
  const [orgTree, setOrgTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [treeError, setTreeError] = useState(null);

  // Load abuse alerts on mount
  useEffect(() => { loadAbuseAlerts(); }, []);

  // Load org tree on mount
  useEffect(() => {
    loadOrgTree();
  }, []);

  const loadOrgTree = async () => {
    setTreeLoading(true);
    setTreeError(null);
    try {
      const res = await userManagementAPI.getOrgTree();
      setOrgTree(res.data.organizations || res.data || []);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load organization tree';
      setTreeError(msg);
      console.error('[UserManagement] Org tree error:', err);
    } finally {
      setTreeLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSearchError(null);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const res = await userManagementAPI.searchUsers(searchQuery.trim());
        setSearchResults(res.data.users || res.data || []);
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Search failed';
        setSearchError(msg);
        console.error('[UserManagement] Search error:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const flaggedUserIds = new Set(abuseAlerts.filter(a => a.user_id).map(a => a.user_id));

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-deep-navy mb-2">User Management</h1>
        <p className="text-storm-blue">Search, browse, and manage users across all organizations.</p>
      </div>

      {/* Abuse Alerts Banner */}
      {!abuseLoading && abuseAlerts.length > 0 && (
        <div className="rounded-card shadow-card bg-red-50 border-2 border-red-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldExclamationIcon className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold text-red-800">
              {abuseAlerts.length} Abuse Alert{abuseAlerts.length !== 1 ? 's' : ''}
            </h2>
          </div>
          <div className="space-y-3">
            {abuseAlerts.map((alert) => (
              <div key={alert.id} className="bg-white rounded-xl border border-red-100 p-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-deep-navy">
                    {alert.user_name || 'Anonymous'}
                    {alert.user_email && <span className="text-sm text-storm-blue ml-2">({alert.user_email})</span>}
                  </p>
                  <p className="text-sm text-storm-blue mt-1">
                    <span className="font-medium text-red-700">{alert.flag_type.replace(/_/g, ' ')}</span>
                    {' — '}
                    {new Date(alert.created_at).toLocaleString()}
                  </p>
                  {alert.details?.totalSearches24h && (
                    <p className="text-xs text-red-600 font-medium mt-1">
                      {alert.details.totalSearches24h} searches in 24h (limit: 20)
                    </p>
                  )}
                  {alert.details?.totalJoinRequests24h && (
                    <p className="text-xs text-red-600 font-medium mt-1">
                      {alert.details.totalJoinRequests24h} join requests in 24h (limit: 3)
                    </p>
                  )}
                  {alert.details?.recentQueries?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Queries: {alert.details.recentQueries.map(q => `"${q}"`).join(', ')}
                    </p>
                  )}
                  {alert.details?.recentJoinRequests?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Orgs: {alert.details.recentJoinRequests.join(', ')}
                    </p>
                  )}
                  {!alert.user_id && alert.ip_address && (
                    <p className="text-xs text-gray-500 mt-1">IP: {alert.ip_address}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {alert.user_status === 'suspended' ? (
                    <span className="text-xs font-medium text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full">Already suspended</span>
                  ) : alert.user_id ? (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Suspend ${alert.user_name || alert.user_email}? This will revoke their sessions and block login.`)) return;
                        try {
                          await userManagementAPI.updateUser(alert.user_id, { status: 'suspended' });
                          await userManagementAPI.updateAbuseAlert(alert.id, { status: 'suspended' });
                          loadAbuseAlerts();
                        } catch (err) {
                          window.alert('Failed to suspend: ' + (err.response?.data?.error || err.message));
                        }
                      }}
                      className="min-h-touch px-4 py-2 rounded-button font-semibold text-white bg-red-600 hover:bg-red-700 transition-all text-sm"
                    >
                      Suspend
                    </button>
                  ) : null}
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await userManagementAPI.updateAbuseAlert(alert.id, { status: 'dismissed' });
                        loadAbuseAlerts();
                      } catch (err) {
                        console.error('Failed to dismiss:', err);
                      }
                    }}
                    className="min-h-touch px-4 py-2 rounded-button font-semibold text-storm-blue bg-gray-100 hover:bg-gray-200 transition-all text-sm"
                  >
                    Dismiss
                  </button>
                  {alert.user_id && (
                    <button
                      onClick={() => navigate(`/user-management/users/${alert.user_id}`)}
                      className="min-h-touch px-4 py-2 rounded-button font-semibold text-storm-blue bg-blue-50 hover:bg-blue-100 transition-all text-sm"
                    >
                      View
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Global User Search */}
      <div className="rounded-card shadow-card bg-white p-6 mb-6">
        <h2 className="text-xl font-semibold text-deep-navy mb-4">Global User Search</h2>

        {/* Search input */}
        <div className="relative">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-sky-blue" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="w-full pl-12 pr-4 py-3 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px]"
            autoFocus
          />
          {searchLoading && (
            <ArrowPathIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 text-storm-blue animate-spin" />
          )}
        </div>

        {/* Search results */}
        <div className="mt-4">
          {searchError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl">
              <p className="text-sm">{searchError}</p>
            </div>
          )}

          {!searchQuery.trim() && !searchResults && (
            <p className="text-center text-sky-blue py-6">Type to search users by name, email, or phone number...</p>
          )}

          {searchResults && searchResults.length === 0 && (
            <p className="text-center text-sky-blue py-6">No results found for "{searchQuery}"</p>
          )}

          {searchResults && searchResults.length > 0 && (
            <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  onClick={() => navigate(`/user-management/users/${user.id}`)}
                  className="w-full text-left px-4 py-3 min-h-touch hover:bg-gray-50 transition-all flex items-center gap-3"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-deep-navy text-cloud-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {(user.first_name?.[0] || '').toUpperCase()}{(user.last_name?.[0] || '').toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-deep-navy truncate">
                      {user.first_name} {user.last_name}
                    </p>
                    <p className="text-sm text-sky-blue truncate">{user.email}</p>
                    {user.phone && (
                      <p className="text-xs text-sky-blue">{user.phone}</p>
                    )}
                  </div>

                  {/* Badges */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {flaggedUserIds.has(user.id) && (
                      <span className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0" title="Abuse flagged" />
                    )}
                    {user.org_count != null && (
                      <span className="text-xs font-medium text-storm-blue bg-blue-50 px-2 py-0.5 rounded-full">
                        {user.org_count} org{user.org_count !== 1 ? 's' : ''}
                      </span>
                    )}
                    {user.group_count != null && (
                      <span className="text-xs font-medium text-storm-blue bg-purple-50 px-2 py-0.5 rounded-full">
                        {user.group_count} group{user.group_count !== 1 ? 's' : ''}
                      </span>
                    )}
                    <StatusBadge status={user.status} />
                  </div>

                  <ChevronRightIcon className="w-4 h-4 text-sky-blue flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Organization Browser */}
      <div className="rounded-card shadow-card bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-deep-navy">Organization Browser</h2>
          <button
            onClick={loadOrgTree}
            disabled={treeLoading}
            className="min-h-touch px-4 py-2 rounded-button text-sm font-semibold text-storm-blue hover:bg-gray-100 transition-all active:scale-[0.98] flex items-center gap-2"
          >
            <ArrowPathIcon className={`w-4 h-4 ${treeLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <OrgTreeBrowser
          orgTree={orgTree}
          loading={treeLoading}
          error={treeError}
          onRetry={loadOrgTree}
        />
      </div>
    </div>
  );
}
